import { makeMembershipGuard } from './object';

/** Called with the source index and value of each entry dropped for not being a known member. */
export type DropReporter = (index: number, value: unknown) => void;

/** Keep only known members of `canonical`, in first-seen order, dropping unknowns and duplicates. */
export function dedupeKnown<T extends string>(
  values: readonly unknown[] | null | undefined,
  canonical: readonly T[],
  onDrop?: DropReporter,
): T[] {
  if (!Array.isArray(values)) return [];
  const isMember = makeMembershipGuard(canonical);
  const seen = new Set<T>();
  const result: T[] = [];

  values.forEach((value, index) => {
    // Unknown entries are data loss worth reporting; a repeated known entry loses nothing.
    if (!isMember(value)) {
      onDrop?.(index, value);
      return;
    }
    if (seen.has(value)) return;
    result.push(value);
    seen.add(value);
  });

  return result;
}

/** {@link dedupeKnown}, then append any `canonical` members missing from the input, in canonical order. */
export function normalizeOrder<T extends string>(
  values: readonly unknown[] | null | undefined,
  canonical: readonly T[],
  onDrop?: DropReporter,
): T[] {
  const ordered = dedupeKnown(values, canonical, onDrop);
  const seen = new Set<T>(ordered);

  for (const item of canonical) {
    if (!seen.has(item)) ordered.push(item);
  }

  return ordered;
}
